import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {css} from "@emotion/react";
import {breakpoints} from "~styles/global";
import Button from "~components/blocks/Button";
import Select from "~components/blocks/Select";
import CheckBox from "~components/blocks/CheckBox";
import sendToMailchimp from "~utils/sendToMailchimp";
import encode from "~utils/encode";
import RichText from "~components/RichText";
import conditionalAddress from "~utils/conditionalAddress";

const ContactForm = ({
  className,
  formName,
  dropdownBackground,
  productName,
  successMessage,
  conditionalSending,
}) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [netlifyError, setNetlifyError] = useState(false);
  const [loading, setLoading] = useState();

  const {mobile} = breakpoints;

  useEffect(() => {
    if (productName) setValues({...values, product: productName});
  }, []);

  const validate = (e) => {
    e.preventDefault();
    const newErrors = [];

    if (!values.name)
      newErrors.push({
        field: "name",
        message: "The name field is required",
      });

    if (!values.email)
      newErrors.push({
        field: "email",
        message: "The email field is required",
      });

    if (!values.state)
      newErrors.push({
        field: "state",
        message: "The state field is required",
      });

    if (!values.message)
      newErrors.push({
        field: "message",
        message: "The message field is required",
      });

    if (values.email && (!values.email.includes("@") || !values.email.includes(".")))
      newErrors.push({
        field: "email",
        message: "Please check your email address",
      });

    if (newErrors.length === 0) {
      submit();
    } else {
      setErrors(newErrors);
    }
  };

  // const submit = () => {
  // 	let formToSend = formName
  // 	if(conditionalSending && values.state === 'vic'){
  // 		formToSend = formName + ' VIC'
  // 	} else if (conditionalSending && values.state === 'nsw') {
  // 		formToSend = formName + ' NSW'
  // 	}
  //   const data = encode({"form-name": formToSend, ...values})
  //   if(window.location.hostname === "localhost"){
  //     setSuccess(true)
  //     if(values.email && values.newsletter){
  //       sendToMailchimp(values.email, null, null, (err, msg) => console.log(err,msg))
  //     }
  //   } else {
  //     axios.post(window.location.pathname, data, {
  //       headers: { "Content-Type": "application/x-www-form-urlencoded" },
  //     }).then(res => {
  //       setSuccess(true)
  //       if(values.email && values.newsletter){
  //         sendToMailchimp(values.email)
  //       }
  //     })
  //     .catch(err =>
  //       setNetlifyError(true)
  //     )
  //   }
  // }

  const submit = () => {
    const msg = {
      to: conditionalAddress(values.state),
      // to: 'jack@formwork.build',
      from: {
        email: "noreply@misura.com.au",
        name: "misura.com.au",
      },
      replyTo: {
        email: values.email,
      },
      template_id: "d-727a31a6fe8346949328dfece26d014f",
      dynamic_template_data: {
        subject: productName ? "New product Enquiry" : "New enquiry",
        headline: "New message from the Misura contact form",
        fields: Object.keys(values).map((key) => ({
          label: key,
          value: values[key],
        })),
      },
    };

    setLoading(true);

    axios
      .post("/api/trigger-sendgrid", msg)
      // axios.post('https://misura.gatsbyjs.io/api/trigger-sendgrid', msg)
      .then((res) => {
        setSuccess(true);
        window.gtag("event", formName, {
          event_category: "Form Submission",
          event_label: window.location.pathname,
        });
        setLoading(false);
        if (values.email && values.newsletter) {
          sendToMailchimp(values.email);
        }
      })
      .catch((err) => {
        setNetlifyError(true);
        setLoading(false);
      });
  };

  const errorFields = errors.map((error) => error.field);

  return (
    <div>
      <div
        className={className}
        css={css`
          position: relative;
        `}
      >
        <h5
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            opacity: ${success ? "1" : "0"};
            transition: opacity 0.3s 0.3s;
            pointer-events: ${success ? "all" : "none"};
          `}
        >
          <RichText content={successMessage} />
        </h5>
        <form
          method="POST"
          data-netlify="true"
          name={formName}
          noValidate
          onSubmit={(e) => (loading ? null : validate(e))}
          css={css`
            opacity: ${success ? "0" : "1"};
            transition: opacity 0.3s;
            pointer-events: ${success ? "none" : "all"};
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-column-gap: 20px;
            ${mobile} {
              display: block;
            }
          `}
        >
          <input type="hidden" name="form-name" value={formName} />
          <input
            type="text"
            placeholder="Full Name*"
            id="name"
            name="name"
            onChange={(e) => setValues({...values, name: e.target.value})}
          />
          <input
            type="email"
            id="email"
            name="email"
            css={css`
              border-color: ${errorFields.includes("email") ? "var(--orange)" : "var(--black)"};
            `}
            placeholder="Email*"
            onChange={(e) => setValues({...values, email: e.target.value})}
          />
          <input
            type="phone"
            id="phone"
            name="phone"
            placeholder="Phone Number"
            onChange={(e) => setValues({...values, phone: e.target.value})}
          />
          <Select
            placeholder="State*"
            onChange={(value) => setValues({...values, state: value.value})}
            name="state"
            background={dropdownBackground}
            options={[
              {value: "vic", label: "Victoria"},
              {value: "nsw", label: "New South Wales"},
              {value: "qld", label: "Queensland"},
              {value: "wa", label: "Western Australia"},
              {value: "sa", label: "South Australia"},
              {value: "nt", label: "Northern Territory"},
              {value: "act", label: "ACT"},
              {value: "tas", label: "Tasmania"},
            ]}
          />
          <textarea
            name="message"
            id="message"
            onChange={(e) => setValues({...values, message: e.target.value})}
            placeholder="Your enquiry"
            css={css`
              grid-column: span 2;
              height: 200px;
              margin-bottom: 40px;
              margin-top: 16px;
              ${mobile} {
                margin-top: 30px;
                margin-bottom: 36px;
                height: 95px;
              }
            `}
          />
          <CheckBox
            id="subscribe-contact"
            name="checkbox"
            hiddenName="newsletter"
            onChange={(value) => setValues({...values, newsletter: value})}
            css={css`
              grid-column: span 2;
              margin-bottom: 40px;
              ${mobile} {
                margin-bottom: 26px;
              }
            `}
          >
            Subscribe to our newsletter to receive news about promotions, products and more.
          </CheckBox>

          {productName && <input type="hidden" value={productName} id="product" name="product" />}
          {errors?.length > 0 && (
            <div
              css={css`
                grid-column: span 2;
                margin-top: -20px;
                margin-bottom: 20px;
                color: var(--orange);
              `}
            >
              {errors.map((error) => (
                <p className="h5">{error.message}</p>
              ))}
            </div>
          )}
          <Button onClick={() => console.log("values", values)} type="submit">
            Submit
          </Button>
        </form>
      </div>
      {conditionalSending && (
        <div
          css={css`
            display: none;
          `}
        >
          <form method="POST" data-netlify="true" name={formName + " NSW"}>
            <input type="hidden" name="form-name" value={formName + " NSW"} />
            <input type="text" name="name" />
            <input type="email" name="email" />
            <input type="phone" name="phone" />
            <select name="state" />
            <textarea name="message" />
            <input type="hidden" name="newsletter" />
            <input type="hidden" id="product" name="product" />
          </form>
          <form method="POST" data-netlify="true" name={formName + " VIC"}>
            <input type="hidden" name="form-name" value={formName + " VIC"} />
            <input type="text" name="name" />
            <input type="email" name="email" />
            <input type="phone" name="phone" />
            <select name="state" />
            <textarea name="message" />
            <input type="hidden" name="newsletter" />
            <input type="hidden" id="product" name="product" />
          </form>
        </div>
      )}
    </div>
  );
};

ContactForm.propTypes = {
  dropdownBackground: PropTypes.string,
  formName: PropTypes.string.isRequired,
  productName: PropTypes.string,
  successMessage: PropTypes.array,
};

ContactForm.defaultProps = {
  formName: "Contact",
  dropdownBackground: "#ffffff",
};

export default ContactForm;
